import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserIdentityIntakeFieldErrors, type IdentityIntakeField } from '../../../features/flow/utils';

interface UserIdentityIntakeScreenBodyProps {
  answers: Record<string, unknown>;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  forwardLabel: string;
  title?: string;
  onContinue: () => void;
  busy: boolean;
  canContinue: boolean;
  showValidationErrors: boolean;
}

export function UserIdentityIntakeScreenBody({
  answers,
  setAnswers,
  forwardLabel,
  title,
  onContinue,
  busy,
  canContinue,
  showValidationErrors,
}: UserIdentityIntakeScreenBodyProps) {
  const { t } = useTranslation('common');
  const fullName = String(answers.full_name ?? '');
  const email = String(answers.email ?? '');
  const nif = String(answers.nif ?? '');

  const fieldErrors = useMemo(() => getUserIdentityIntakeFieldErrors(answers), [answers]);

  const handleChange = useCallback(
    (key: string, value: string) => {
      setAnswers((prev) => ({ ...prev, [key]: value }));
    },
    [setAnswers]
  );

  const inputClass =
    'w-full border rounded-lg px-3 py-2.5 text-sm text-[#131315] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6ECFCF] focus:border-transparent transition-colors';

  const fieldBorder = (field: IdentityIntakeField) =>
    showValidationErrors && fieldErrors[field] ? 'border-red-500' : 'border-gray-300';

  const errorText = (field: IdentityIntakeField) => {
    const key = fieldErrors[field];
    if (!showValidationErrors || !key) return null;
    return (
      <p className="text-red-600 text-sm mt-0.5" role="alert">
        {t(key)}
      </p>
    );
  };

  return (
    <div className="flex w-full min-h-[min(70vh,720px)] flex-col items-center justify-center self-stretch py-6 md:py-10">
      <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-lg p-6 w-full max-w-[608px] flex flex-col gap-8">
        <h2 className="text-center text-[22px] font-semibold text-[#131315]">
          {title || t('identity_intake.heading', { defaultValue: 'Just fill this, and we can start!' })}
        </h2>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-base font-medium text-[#131315]">{t('identity_intake.full_name_label', { defaultValue: 'Full name' })} *</label>
            <input
              type="text"
              placeholder={t('identity_intake.full_name_placeholder', { defaultValue: 'Your name' })}
              value={fullName}
              onChange={(e) => handleChange('full_name', e.target.value)}
              className={`${inputClass} ${fieldBorder('full_name')}`}
              autoComplete="name"
              aria-invalid={showValidationErrors && Boolean(fieldErrors.full_name)}
            />
            {errorText('full_name')}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-base font-medium text-[#131315]">{t('identity_intake.email_label', { defaultValue: 'Email address' })} *</label>
            <input
              type="email"
              placeholder={t('identity_intake.email_placeholder', { defaultValue: 'Email' })}
              value={email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`${inputClass} ${fieldBorder('email')}`}
              autoComplete="email"
              aria-invalid={showValidationErrors && Boolean(fieldErrors.email)}
            />
            {errorText('email')}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-base font-medium text-[#131315]">{t('identity_intake.tax_id_label', { defaultValue: 'Tax ID (NIF / NIE)' })} *</label>
            <input
              type="text"
              placeholder="#########"
              value={nif}
              onChange={(e) => handleChange('nif', e.target.value.toUpperCase())}
              className={`${inputClass} ${fieldBorder('nif')}`}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={showValidationErrors && Boolean(fieldErrors.nif)}
            />
            {errorText('nif')}
          </div>
        </div>

        <div className="flex justify-center">
          <button
            type="button"
            disabled={busy}
            onClick={onContinue}
            className={[
              'w-full max-w-[400px] min-h-[40px] rounded-lg text-[13px] font-semibold flex items-center justify-center transition-colors',
              canContinue && !busy
                ? 'bg-[#6ECFCF] hover:bg-[#069694] text-black'
                : 'bg-[#94DDDC] text-[#131315] cursor-not-allowed',
            ].join(' ')}
          >
            {busy ? '…' : forwardLabel}
          </button>
        </div>
      </div>
    </div>
  );
}